import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { useDeviceSize } from '@atlassian/learning-components';

import { Asset, AtLink } from 'src/graphql/generated/graphql';
import AnimatedLink from 'src/pages/hubs/shared/children/AnimatedLink';

type FloatingImageBlockProps = {
  textContent: string;
  imagePosition?: boolean;
  image: Asset;
  cta?: AtLink;
  backgroundImage?: Asset;
  mobileBackgroundImage?: Asset;
};

const FloatingImageBlock: React.FC<FloatingImageBlockProps> = ({
  textContent,
  imagePosition,
  image,
  cta,
  backgroundImage,
  mobileBackgroundImage,
}) => {
  const imageUrl = image?.url;
  const imageAlt = image?.title;
  const backgroundImageUrl = backgroundImage?.url;
  const mobileBackgroundImageUrl = mobileBackgroundImage?.url;

  const deviceWidth = useDeviceSize();
  const isMobile = deviceWidth < 768;

  const getFlexDirection = (): 'column' | 'row' | 'row-reverse' => {
    if (isMobile) {
      return 'column';
    }

    return imagePosition ? 'row' : 'row-reverse';
  };

  return (
    <div
      data-testid="floating-image-block"
      css={containerStyle}
      style={{
        backgroundImage: `url(${isMobile ? mobileBackgroundImageUrl : backgroundImageUrl})`,
        flexDirection: getFlexDirection(),
      }}
    >
      <div>
        <img src={imageUrl} alt={imageAlt} />
      </div>
      <div css={contentContainerStyles}>
        <div data-testid="text" css={textContentStyle}>
          {textContent}
        </div>
        {cta && <AnimatedLink link={cta.link} label={cta.label} />}
      </div>
    </div>
  );
};

const containerStyle = css({
  margin: '0 auto',
  display: 'flex',
  padding: `${token('space.200')} ${token('space.300')}`,
  alignItems: 'center',
  gap: token('space.300'),
  alignSelf: 'stretch',
  maxWidth: '814px',

  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  minHeight: '153px',

  [media.below.md]: {
    backgroundSize: 'cover',
    padding: `${token('space.400')} ${token('space.300')}`,
  },
});

const textContentStyle = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: '24px',
  color: token('color.text'),
});

const contentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
});

export default FloatingImageBlock;
